import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showAPI } from '../utils/api';
import { CalendarBlank, MapPin, Trophy, MagnifyingGlass, FunnelSimple } from '@phosphor-icons/react';

const ShowList = () => {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    loadShows();
  }, []);

  const loadShows = async () => {
    try {
      const { data } = await showAPI.list('PUBLIC');
      setShows(data);
    } catch (error) {
      console.error('Failed to load shows');
    } finally {
      setLoading(false);
    }
  };

  const filtered = shows.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) || s.location.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'ALL' || s.state === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-gradient-to-r from-[#0096FF]/10 to-[#00D4FF]/5 border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3" style={{fontFamily: 'Outfit'}} data-testid="shows-page-title">
            Upcoming Shows
          </h1>
          <p className="text-[#9E9E9E] text-lg">Browse and enter barrel racing events</p>
          <button
            onClick={() => navigate('/series/public')}
            data-testid="view-series-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#161616] border border-[#262626] hover:border-[#0096FF] text-white text-sm font-semibold rounded-md transition-colors"
          >
            View Show Series
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or location..."
              className="w-full pl-10 pr-4 py-3 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
              data-testid="show-search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['ALL', 'PUBLISHED', 'LIVE', 'COMPLETED'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase()}`}
                className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                  filter === f ? 'bg-[#0096FF] text-white' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white'
                }`}
              >
                {f === 'ALL' ? 'All' : f === 'PUBLISHED' ? 'Open' : f === 'LIVE' ? 'Live' : 'Results'}
              </button>
            ))}
          </div>
        </div>

        {/* Shows Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-[#161616] border border-[#262626] rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-[#262626] rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-[#262626] rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-[#262626] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Trophy size={48} className="text-[#262626] mx-auto mb-4" />
            <p className="text-[#9E9E9E] text-lg">No shows found</p>
            <p className="text-[#555] text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((show) => {
              const isLive = show.state === 'LIVE';
              const isOpen = show.state === 'PUBLISHED';
              const isCompleted = show.state === 'COMPLETED';
              return (
                <div
                  key={show.id}
                  onClick={() => navigate(`/shows/${show.id}`)}
                  className={`bg-[#161616] border rounded-lg overflow-hidden cursor-pointer group transition-all duration-200 hover:-translate-y-1 ${
                    isLive ? 'border-[#00D4FF]/40 hover:border-[#00D4FF]' : 'border-[#262626] hover:border-[#0096FF]/50'
                  }`}
                  data-testid={`show-card-${show.id}`}
                >
                  {/* Status Bar */}
                  <div className={`px-4 py-2 flex items-center justify-between ${
                    isLive ? 'bg-[#00D4FF]/10' : isOpen ? 'bg-[#34C759]/10' : isCompleted ? 'bg-[#0096FF]/10' : 'bg-[#9E9E9E]/5'
                  }`}>
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      isLive ? 'text-[#00D4FF]' : isOpen ? 'text-[#34C759]' : isCompleted ? 'text-[#0096FF]' : 'text-[#9E9E9E]'
                    }`}>
                      {isLive ? 'Live Now' : isOpen ? 'Entries Open' : isCompleted ? 'Completed' : show.state}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Mail-in-only chip — surfaces the producer's
                          `online_entries_disabled` flag on the card so
                          riders scanning shows can see BEFORE clicking
                          in that this event requires mailing an entry
                          form + check (no online checkout). */}
                      {show.online_entries_disabled && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded text-[10px] font-bold uppercase tracking-wider"
                          data-testid={`mail-in-chip-${show.id}`}
                          title="Online entries are disabled — mail-in only"
                        >
                          Mail-In Only
                        </span>
                      )}
                      {/* v218 — Co-sanctioned chip. Surfaces the fact
                          that other associations also count this show
                          toward their standings so riders shopping for
                          points know at a glance. */}
                      {(show.co_sanctioning_org_ids || []).length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30 rounded text-[10px] font-bold uppercase tracking-wider"
                          data-testid={`co-sanctioned-chip-${show.id}`}
                          title={`Co-sanctioned with ${show.co_sanctioning_org_ids.length} other organization${show.co_sanctioning_org_ids.length > 1 ? 's' : ''}`}
                        >
                          Co-Sanctioned
                        </span>
                      )}
                      {isLive && <span className="w-2 h-2 bg-[#00D4FF] rounded-full animate-pulse"></span>}
                    </div>
                  </div>

                  {/* Showbill Thumbnail */}
                  {show.showbill_url && (
                    <div className="w-full h-40 bg-[#0A0A0A] overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${show.showbill_url}`}
                        alt={`${show.name} showbill`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        data-testid={`show-thumbnail-${show.id}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}

                  <div className="p-5">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#0096FF] transition-colors" style={{fontFamily: 'Outfit'}}>
                      {show.name}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-[#9E9E9E] text-sm">
                        <CalendarBlank size={14} />
                        <span>{new Date(show.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(show.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#9E9E9E] text-sm">
                        <MapPin size={14} />
                        <span>{show.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#9E9E9E] text-sm">
                        <Trophy size={14} />
                        <span>{show.classes.length} {show.classes.length === 1 ? 'class' : 'classes'}</span>
                      </div>
                    </div>

                    {isLive && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/shows/${show.id}/results`); }}
                          className="flex-1 py-2.5 bg-[#00D4FF]/15 hover:bg-[#00D4FF]/25 text-[#00D4FF] font-semibold rounded-md text-sm transition-colors border border-[#00D4FF]/30"
                          data-testid={`live-results-${show.id}`}
                        >
                          View Live Results
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/shows/${show.id}/enter`); }}
                          className="flex-1 py-2.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md text-sm transition-colors"
                          data-testid={`enter-show-${show.id}`}
                        >
                          Enter Show
                        </button>
                      </div>
                    )}
                    {isOpen && !isLive && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/shows/${show.id}/enter`); }}
                        className="w-full py-2.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md text-sm transition-colors"
                        data-testid={`enter-show-${show.id}`}
                      >
                        Enter Show
                      </button>
                    )}
                    {isCompleted && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/shows/${show.id}/results`); }}
                        className="w-full py-2.5 bg-[#0096FF]/15 hover:bg-[#0096FF]/25 text-[#0096FF] font-semibold rounded-md text-sm transition-colors border border-[#0096FF]/30"
                        data-testid={`view-results-${show.id}`}
                      >
                        View Results
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowList;
